<script setup lang="ts">
import CareerTeamWithHeader from '@/domains/career/components/career-teamWithHeader/career-teamWithHeader.vue'
import CareerMainRecruitmentProcess from '@/domains/career/main/components/career-main-recruitmentProcess/career-main-recruitmentProcess.vue'
import CareerMainBoxThreeOnRow from '@/domains/career/main/components/career-main-boxThreeOnRow/career-main-boxThreeOnRow.vue'
import CareerMainBenefits from '@/domains/career/main/components/career-main-benefits/career-main-benefits.vue'
import CareerMainHireRegions from '@/domains/career/main/components/career-main-hireRegions/career-main-hireRegions.vue'
import CareerCtaBoxes from '@/domains/career/components/career-ctaBoxes/career-ctaBoxes.vue'
import CareerWideCtaBox from '@/domains/career/components/career-wideCtaBox/career-wideCtaBox.vue'
import CareerEmptyResults from '@/domains/career/components/career-emptyResults/career-emptyResults.vue'
import CareerSearchBar from '@/domains/career/components/career-searchBar/career-searchBar.vue'
import CareerSearchResults from '@/domains/career/components/career-searchResults/career-searchResults.vue'
import CareerMainVimeo from '@/domains/career/main/components/career-main-vimeo/career-main-vimeo.vue'
import CareerSubMenu from '@/domains/career/components/career-subMenu/career-subMenu.vue'
import CareerScrollProgress from '@/domains/career/components/career-scrollProgress/career-scrollProgress.vue'
import CareerMainConnectedUs from '@/domains/career/main/components/career-main-connectedUs/career-main-connectedUs.vue'
import CareerMainOurAwards from '@/domains/career/main/components/career-main-ourAwards/career-main-ourAwards.vue'
import UiPhotoWithDescription from '@/components/UiPhotoWithDescription/UiPhotoWithDescription.vue'
import CareerMainOffices from '~/domains/career/main/components/career-main-offices/career-main-offices.vue'
import CareerTeamMembers from '~/domains/career/components/career-teamMembers/career-team-members.vue'
import useErecruiter from '@/composables/useErecruiter'
import type { IOffer, IQuery } from '@/types/shared-types'

import { useCareerPage } from '@/domains/career/composables/useCareerPage'

const { t } = useI18n()
const { getOffers } = useErecruiter()
const { getCampaignToken } = useMapDataNewsletter()
const { isMobile } = useBSBreakpoints()

const { data, setSeo } = await useCareerPage()

if (data.value?.content?.items?.seo) {
  setSeo(data.value.content.items.seo)
}

definePageMeta({
  layout: 'white-menu',
})

const offers = ref<IOffer[]>([])
const currentOffers = ref<IOffer[]>([])
const hasUsedSearch = ref(false)
const onSearch = async (data: IQuery) => {
  offers.value = await getOffers(data)
  if (!offers.value.length) {
    currentOffers.value = await getOffers()
  }
  hasUsedSearch.value = true
}

const onShowAll = async () => {
  await navigateTo('/o-benefit-systems/kariera/wszystkie-oferty', { replace: true })
}

</script>

<template>
  <div class="career career-page">
    <CareerSubMenu />

    <CareerScrollProgress />

    <Hero
      v-if="data?.content?.items.hero"
      v-bind="{
        ...data?.content?.items.hero,
        video: '/videos/career/videoHero/playback.mp4',
        button: {
          text: data?.content?.items?.hero.firstButton?.label,
          to: data?.content?.items?.hero.firstButton?.to,
          icon: data?.content?.items?.hero.firstButton?.icon?.src
        }
      }"
      :is-centered="isMobile"
    />

    <CareerSearchBar
      header="Znajdź miejsce dla siebie"
      @submit="onSearch"
      @show-all="onShowAll"
    />

    <div
      v-if="hasUsedSearch"
      class="offers__wrapper"
    >
      <div v-if="offers.length">
        <CareerSearchResults
          header="Twoje wyszukiwane oferty:"
          text-link="Pokaż więcej wyników"
          :cards="offers"
          :show-results="true"
          :with-wrapper="true"
        />
      </div>

      <CareerEmptyResults
        v-else
        :current-offers="currentOffers"
      />
    </div>

    <div
      v-if="!hasUsedSearch && data?.content?.items.jobDescription"
      class="career__photoWrapper"
    >
      <UiPhotoWithDescription
        class="career__divider"
        v-bind="data?.content?.items.jobDescription"
      />
    </div>

    <div
      v-if="!hasUsedSearch && data?.offers && data?.content?.items?.latestOffers"
      :id="t('career.submenu.hash.latestOffers')"
      class="last-offer__wrapper"
    >
      <CareerSearchResults
        v-bind="{...data?.content?.items?.latestOffers, cards: data?.offers}"
      />
    </div>

    <div :id="t('career.submenu.hash.hireAreas')">
      <CareerMainHireRegions
        v-if="data?.content?.items.hireRegions"
        v-bind="{...data?.content?.items.hireRegions}"
      />
    </div>

    <div
      v-if="data?.content?.items.boxPresentation"
      :id="t('career.submenu.hash.aboutUs')"
      class="career__articles"
    >
      <CareerMainBoxThreeOnRow
        v-bind="data?.content?.items.boxPresentation"
      />
    </div>

    <BSNumbers
      v-if="data?.content?.items.bsNumbers"
      class="career__divider"
      v-bind="data?.content?.items.bsNumbers"
    />

    <div>
      <CareerMainOurAwards />
    </div>

    <div>
      <CareerMainConnectedUs />
    </div>

    <CareerMainVimeo />

    <div :id="t('career.submenu.hash.benefits')">
      <CareerMainBenefits
        v-if="data?.content?.items.benefits"
        class="career__divider"
        v-bind="{...data?.content?.items.benefits}"
      />
    </div>

    <!-- <div>
      <CareerMainEmployeesPublications />
    </div> -->

    <div :id="t('career.submenu.hash.process')">
      <CareerMainRecruitmentProcess
        v-if="data?.content?.items?.recruitmentProcess"
        v-bind="{...data?.content?.items?.recruitmentProcess}"
      />
    </div>

    <UiNewsletter
      :title="$t('newsletter.titles.recruitment.main')"
      checkbox-name="webform[consent#m-ver#Jx]"
      :campaign-token="getCampaignToken('recruitment')"
      :consent="$t('newsletter.consent.recruitment.noExtend')"
      custom-form-id="kariera-glowna-pl"
      subtitle="Zapisz się na nasz newsletter rekrutacyjny, aby dostawać powiadomienia o nowych ofertach"
      version="secondary"
      thank-you-url="/o-benefit-systems/kariera/newsletter/dziekujemy"
    />

    <div
      v-if="data?.content?.items.ctaBoxes"
      class="career__divider"
    >
      <CareerCtaBoxes
        v-if="data?.content?.items.ctaBoxes"
        v-bind="data.content?.items"
      />
    </div>

    <div :id="t('career.submenu.hash.team')">
      <CareerTeamMembers
        v-if="data?.content?.items?.team"
        v-bind="{...data?.content?.items?.team}"
        modal-aria-label="teamModalCareer"
      />
    </div>

    <div :id="t('career.submenu.hash.faq')">
      <CareerTeamWithHeader
        v-if="data?.content?.items.qaDrawer"
        v-bind="{...data?.content?.items?.qaDrawer}"
      />
    </div>

    <div
      v-if="data?.content?.items.map"
      :id="t('career.submenu.hash.offices')"
      class="career__map"
    >
      <CareerMainOffices />
    </div>

    <CareerWideCtaBox
      v-if="data?.content?.items?.wideCtaBox"
      v-bind="data?.content?.items.wideCtaBox"
    />
  </div>
</template>

<style lang="scss" scoped>
  .career {
    &__map {
      margin: 12rem 0;
    }

    &__divider {
      padding-right: 2.4rem;
      padding-left: 2.4rem;
    }

    &__photoWrapper {
      margin: 4rem 0;
    }

    @include atM {
      &__photoWrapper {
        margin: 6rem 0;
      }
    }
  }

  .offers__wrapper {
    padding: 4rem 2.4rem;
    background-color: $gray-100;
    border-bottom: 1px solid $gray-300;

    @include atM {
      padding: 5.6rem 1rem 11.2rem;
    }
  }
</style>

<style lang="scss">
.career {
  .Team-Member {
    width: 100%;
  }
}

@include atXS {
  .career {
    &.career-page .banner {
      padding-top: 10rem;
      padding-bottom: 3.2rem;
    }
  }
}
</style>
